"use client";

import { useEffect, useMemo, useRef, useState } from "react";

interface Props {
  children: React.ReactNode[];
  itemsPerSlide?: number;
  autoPlayDelay?: number;
}

export default function SubPromoSlider({
  children,
  itemsPerSlide = 3,
}: Props) {
  const [responsiveItems, setResponsiveItems] = useState(itemsPerSlide);

  useEffect(() => {
    const updateItems = () => {
      if (window.innerWidth < 768) {
        setResponsiveItems(1);
      } else if (window.innerWidth < 1200) {
        setResponsiveItems(2);
      } else {
        setResponsiveItems(itemsPerSlide);
      }
    };

    updateItems();
    window.addEventListener("resize", updateItems);
    return () => window.removeEventListener("resize", updateItems);
  }, [itemsPerSlide]);

  const slides = useMemo(() => {
    const result = [];
    for (let i = 0; i < children.length; i += responsiveItems) {
      result.push(children.slice(i, i + responsiveItems));
    }
    return result;
  }, [children, responsiveItems]);

  const extendedSlides = [
    slides[slides.length - 1],
    ...slides,
    slides[0],
  ];

  const [index, setIndex] = useState(1);
  const [transition, setTransition] = useState(true);
  const [isPaused, setIsPaused] = useState(false);

  const next = () => setIndex((i) => i + 1);
  const prev = () => setIndex((i) => i - 1);

  useEffect(() => {
    if (index === extendedSlides.length - 1) {
      setTimeout(() => {
        setTransition(false);
        setIndex(1);
      }, 300);
    }

    if (index === 0) {
      setTimeout(() => {
        setTransition(false);
        setIndex(extendedSlides.length - 2);
      }, 300);
    }
  }, [index, extendedSlides.length]);

  useEffect(() => {
    if (!transition) {
      requestAnimationFrame(() => setTransition(true));
    }
  }, [transition]);

  return (
    <div
      className="position-relative mb-4"
      style={{ padding: '0 40px' }}
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => setIsPaused(false)}
    >
      <div style={{ overflow: 'hidden' }}>
        <div
          className="d-flex"
          style={{
            transform: `translateX(-${index * 100}%)`,
            transition: transition ? "transform 0.4s ease" : "none",
          }}
        >
          {extendedSlides.map((group, slideIndex) => (
            <div className="flex-shrink-0 w-100" key={slideIndex}>
              <div className="row g-3">
                {group.map((card, i) => (
                  <div key={i} className="col-12 col-md-6 col-xl-4">
                    {card}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Prev Arrow */}
      <button
        onClick={prev}
        style={{
          position: 'absolute',
          left: 0,
          top: '50%',
          transform: 'translateY(-50%)',
          zIndex: 10,
          width: '32px',
          height: '32px',
          borderRadius: '50%',
          border: 'none',
          background: 'var(--brand-primary)',
          color: '#fff',
          fontSize: '18px',
          lineHeight: 1,
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        ‹
      </button>

      {/* Next Arrow */}
      <button
        onClick={next}
        style={{
          position: 'absolute',
          right: 0,
          top: '50%',
          transform: 'translateY(-50%)',
          zIndex: 10,
          width: '32px',
          height: '32px',
          borderRadius: '50%',
          border: 'none',
          background: 'var(--brand-primary)',
          color: '#fff',
          fontSize: '18px',
          lineHeight: 1,
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        ›
      </button>
    </div>
  );
}
